import React, { useEffect, useCallback, useRef, useId } from 'react'
import { X } from 'lucide-react'

interface ModalProps {
  open: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  width?: 'sm' | 'md' | 'lg' | 'xl'
}

const widthMap = {
  sm: '400px',
  md: '500px',
  lg: '640px',
  xl: '800px',
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  width = 'md',
}) => {
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (open) {
      const previousFocus = document.activeElement as HTMLElement | null
      const previousOverflow = document.body.style.overflow
      const dialog = dialogRef.current
      const controls = () => Array.from(dialog?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]') ?? [])
      if (!dialog?.contains(document.activeElement)) (controls()[0] ?? dialog)?.focus()
      const trapFocus = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return
        const items = controls()
        const first = items[0]
        const last = items[items.length - 1]
        if (!first) { e.preventDefault(); dialog?.focus(); return }
        if (e.shiftKey && (document.activeElement === first || !dialog?.contains(document.activeElement))) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && (document.activeElement === last || !dialog?.contains(document.activeElement))) { e.preventDefault(); first.focus() }
      }
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('keydown', trapFocus)
      document.body.style.overflow = 'hidden'
      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.removeEventListener('keydown', trapFocus)
        document.body.style.overflow = previousOverflow
        if (previousFocus?.isConnected) previousFocus.focus()
      }
    }
  }, [open, handleEscape])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Content */}
      <div
        className="relative rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95"
        style={{
          backgroundColor: 'var(--bg-primary)',
          width: widthMap[width],
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 80px)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '1px solid var(--separator)' }}
        >
          <h2 id={titleId} className="text-[16px] font-semibold" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h2>
          <button
            aria-label="Закрыть окно"
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:opacity-70 transition-opacity"
            style={{ color: 'var(--text-hint)' }}
          >
            <X size={18} />
          </button>
        </div>
        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
          {children}
        </div>
      </div>
    </div>
  )
}
